import { forwardRef, memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import classnames from 'classnames';

import '@hh.ru/magritte-common-focus-visible';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import type { PolymorphicForwardRefRenderFunc, PolymorphicComponentWithRef } from '@hh.ru/magritte-types';
import { Badge, ComponentWithBadge } from '@hh.ru/magritte-ui-badge';
import { useScrollContext } from '@hh.ru/magritte-ui-scrollable-container';
import type { TabProps } from '@hh.ru/magritte-ui-tabs/types';
import { TooltipHover } from '@hh.ru/magritte-ui-tooltip';
import { Text } from '@hh.ru/magritte-ui-typography';

import styles from './tabs.less';

const TabForwardRefRenderFunc: PolymorphicForwardRefRenderFunc<TabProps, 'button'> = (
    {
        Element = 'button',
        badge = false,
        children,
        'data-qa': dataQa = 'tab',
        focused,
        hidden,
        id,
        itemRef,
        mode = 'primary',
        onClick,
        onSelect,
        postfix,
        selected,
        ...buttonProps
    },
    ref
) => {
    const activatorRef = useRef<HTMLSpanElement>(null);
    const elementRef = useRef<HTMLElement | null>(null);
    const labelRef = useRef<HTMLSpanElement>(null);
    const mouseEventRef = useRef(false);

    const { removeItem, registerItem, scrollToItem } = useScrollContext();

    const scrollItemCallback = useCallback(
        (element: HTMLElement | null) => {
            if (element !== null) {
                elementRef.current = element;
                registerItem(elementRef);
            } else {
                removeItem(elementRef);
                elementRef.current = null;
            }
        },
        [registerItem, removeItem]
    );

    const elementRefCallback = useMultipleRefs<HTMLElement>(scrollItemCallback, itemRef, ref as React.Ref<HTMLElement>);

    const [showTooltip, setShowTooltip] = useState(false);

    useLayoutEffect(() => {
        if (focused && elementRef.current) {
            elementRef.current.focus();
        }
    }, [focused]);

    useLayoutEffect(() => {
        if (selected && elementRef.current) {
            scrollToItem(elementRef);
            if (showTooltip && labelRef.current) {
                labelRef.current.dispatchEvent(new MouseEvent('mouseout', { bubbles: true, cancelable: true }));
            }
        }
    }, [selected, scrollToItem, showTooltip]);

    useLayoutEffect(() => {
        if (
            mode === 'secondary' &&
            labelRef.current !== null &&
            labelRef.current.scrollWidth > labelRef.current.offsetWidth
        ) {
            setShowTooltip(true);
        }
    }, [mode, setShowTooltip]);

    const handleFocus = useCallback(() => {
        if (!mouseEventRef.current) {
            scrollToItem(elementRef);
        }
    }, [scrollToItem]);

    const setMouseEventRef = useCallback(() => (mouseEventRef.current = true), []);

    const resetMouseEventRef = useCallback(() => (mouseEventRef.current = false), []);

    const handleWheel = useCallback(() => {
        if (labelRef?.current) {
            labelRef.current.dispatchEvent(new MouseEvent('mouseout', { bubbles: true, cancelable: true }));
        }
    }, []);

    if (hidden) {
        return null;
    }

    const handleClick: React.MouseEventHandler<HTMLElement> = (event) => {
        onSelect?.(id);
        onClick?.(event);
    };

    const content = (
        <span className={styles.tabContent} ref={activatorRef}>
            <span className={styles.tabLabel} ref={labelRef}>
                <Text Element="span" typography={mode === 'primary' ? 'subtitle-1-semibold' : 'label-3-regular'}>
                    {children}
                </Text>
            </span>
            {postfix && (
                <span className={styles.postfixWrapper} data-qa="tab-postfix">
                    <Text
                        Element="span"
                        typography={mode === 'primary' ? 'subtitle-1-semibold' : 'subtitle-2-semibold'}
                    >
                        <span className={styles.postfix}>{postfix}</span>
                    </Text>
                </span>
            )}
        </span>
    );

    const tabElement = (
        <Element
            {...buttonProps}
            type="button"
            ref={elementRefCallback}
            role="tab"
            data-qa={dataQa}
            onClick={handleClick}
            className={classnames(styles.tab, { [styles.tabSelected]: selected, [styles[`tab_${mode}`]]: !!mode })}
            aria-selected={selected}
            onFocus={handleFocus}
            onMouseDown={setMouseEventRef}
            onMouseLeave={resetMouseEventRef}
            onMouseUp={resetMouseEventRef}
            onWheel={showTooltip ? handleWheel : undefined}
        >
            {content}
            {showTooltip && (
                <TooltipHover activatorRef={activatorRef} placement="top-center">
                    {children}
                </TooltipHover>
            )}
        </Element>
    );

    return (
        <div className={classnames(styles.tabWrapper, { [styles[`tab-wrapper_${mode}`]]: !!mode })}>
            {badge && mode === 'primary' ? (
                <ComponentWithBadge badge={<Badge size="extra-small" />} offset={2}>
                    {tabElement}
                </ComponentWithBadge>
            ) : (
                tabElement
            )}
        </div>
    );
};

const TabComponent: PolymorphicComponentWithRef<TabProps, 'button'> = forwardRef(TabForwardRefRenderFunc);

export const Tab: PolymorphicComponentWithRef<TabProps, 'button'> = memo(TabComponent);
